import {
  DataProjectionCoordinate,
  SceneCoordinate
} from "../projections";
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  Line3,
  Mesh,
  MeshBasicMaterial,
  Scene,
  SphereGeometry,
  Vector2,
  Vector3
} from "three";
import { PanoControls } from "../controls";
import { UnitConverter } from "../utilities";
import { t } from "../../../localization";
import { ViewerProps } from "../main";

Chart.register(zoomPlugin);

export class PointProfile {
  public drawing = false;
  public scene = new Scene();
  public controls: PanoControls;
  public profileChart: ProfileChart;
  public line2D = new Line3();
  public line3D = new Line3();
  private sphere: Mesh;

  private lineWidth = 0.5;
  private hoverColor = 0x099c7b;
  private markerSize = 0.05;

  constructor(controls: PanoControls, props) {
    this.controls = controls;
    this.profileChart = new ProfileChart(this, props);

    this.initSphere();
    this.reset();
  }

  get viewer() {
    return this.controls.viewer;
  }

  get pointclouds() {
    return this.viewer.pointclouds;
  }

  reset() {
    this.line3D = new Line3();
    this.clearScene();
    this.updateUniforms();
  }

  destroy() {
    this.profileChart.destroy();
  }

  initSphere() {
    let geometry = new SphereGeometry(this.markerSize, 20, 20);
    let material = new MeshBasicMaterial({color: this.hoverColor});
    this.sphere = new Mesh(geometry, material);
    this.hideSphere();
  }

  clearScene() {
    if (!this.scene) return;
    this.scene.remove(this.sphere);
  }

  updateUniforms() {
    this.pointclouds.updateCrossSection(this.line3D.start,
      this.line3D.end, this.lineWidth);
  }

  onMouseMove(event) {
    let {coordinate} = this.controls.getPickerResults(event);
    if (coordinate === null) {
      return;
    }

    this.line3D.end = coordinate;
    this.updateUniforms();
  }

  selectStart(coordinate: number[]) {
    this.reset();
    this.profileChart.setData([]);
    this.drawing = true;

    const localPosition = new DataProjectionCoordinate(coordinate)
      .toScene();

    this.line3D.start.copy(localPosition);
    this.line3D.end.copy(localPosition);
  }

  selectEnd() {
    this.line2D = new Line3().copy(this.line3D);
    this.line2D.end.setZ(0);
    this.line2D.start.setZ(0);

    if (this.line3D.distance() === 0) {
      return;
    }

    this.drawing = false;
    this.updateUniforms();
    this.updatePoints();
    this.resetZoom();
  }

  selectCancel() {
    this.reset();
    this.drawing = false;
  }

  updatePoints() {
    let center = new Vector3();
    this.line3D.getCenter(center);
    let distance = this.line3D.distance() + this.lineWidth;

    // Grab points in a radius around our line
    let points = this.pointclouds.pointsInRadius(center, distance);

    // Filter for points that are along our line, with a valid width
    points = points.filter(vector => {
      let linePoint = new Vector3();
      this.line2D.closestPointToPoint(vector, false, linePoint);

      // Calculate distance from line
      let dx = vector.x - linePoint.x;
      let dy = vector.y - linePoint.y;
      let distanceAcrossLine = Math.sqrt(dx**2 + dy**2);
      let validWidth = distanceAcrossLine <= (this.lineWidth / 2.0);

      // Calculate distance along line
      let distanceAlongLine = this.line2D.closestPointToPointParameter(linePoint, false);
      let validDistance = (distanceAlongLine >= 0) && (distanceAlongLine <= 1);

      // Remember our distance travelled
      vector.distance = distanceAlongLine * this.line2D.distance();

      return validDistance && validWidth;
    });

    // Sort by distance along line
    points.sort((a, b) => a.distance - b.distance);

    this.profileChart.setData(points);
    this.profileChart.show();
  }

  drawSphere(position) {
    this.scene.remove(this.sphere);

    // Point to draw is snapped to the line, but with
    // the height of the original point
    let linePoint = new Vector3();
    let distanceClamped = position.distance / this.line2D.distance();
    this.line3D.at(distanceClamped, linePoint);
    linePoint.z = position.z;

    this.sphere.position.copy(linePoint);
    this.scene.add(this.sphere);
  }

  hideSphere() {
    if (!this.sphere) return;
    this.sphere.visible = false;
  }

  showSphere() {
    if (!this.sphere) return;
    this.sphere.visible = true;
  }

  resetZoom() {
    this.profileChart.resetZoom();
  }

  updateUnits(units) {
    this.profileChart.updateUnits(units);
  }

  updateSampling(type) {
    this.profileChart.updateSampling(type);
  }

  getProfileImage() {
    return this.profileChart.chartImageBase64.toBase64Image();
  }
}

class ProfileChart {
  private data = [];
  private sampled = [];
  private displayUnits = "m";
  private samplingType = "highest";
  private samplingRate = 1000;
  private sampleMinimum = 0.02;
  private sampleMaximum = 1.00;

  private chart: Chart;
  private converter = new UnitConverter();
  private pointProfile: PointProfile;

  public setPointProfileOpen: React.Dispatch<boolean>;

  constructor(pointProfile: PointProfile, props: ViewerProps) {
    this.data = [];
    this.sampled = [];
    this.pointProfile = pointProfile;

    this.setPointProfileOpen = props.setPointProfileOpen;
  }

  get viewer() {
    return this.pointProfile.viewer;
  }

  get chartImageBase64() {
    return this.chart;
  }

  init(type, units) {
    const element = document.getElementById('profile-chart') as HTMLCanvasElement;
    const canvas = element.getContext('2d');

    const scales = {
      x: {
        type: 'linear',
        title: {
          display: true,
          text: t('profile.axis-distance')
        },
        ticks: {
          callback: value => {
            const output = this.convert(value);
            return `${output.toFixed(2)} ${this.displayUnits}.`;
          }
        }
      },
      y: {
        type: 'linear',
        title: {
          display: true,
          text: t('profile.axis-height')
        },
        ticks: {
          callback: value => {
            const output = this.convert(value);
            return `${output.toFixed(2)} ${this.displayUnits}.`;
          }
        }
      },
    };

    const tooltip = {
      caretPadding: 10,
      displayColors: false,
      filter: (_, index) => {
        // Return the first result only
        return index === 0;
      },
      callbacks: {
        label: context => {
          let vector = this.sampled[context.dataIndex];
          this.pointProfile.drawSphere(vector);

          const distance = this.convert(context.raw.x);
          const height = this.convert(context.raw.y);

          return [
            t("profile.height", {
              height: height.toFixed(2),
              units: this.displayUnits
            }),
            t("profile.distance", {
              distance: distance.toFixed(2),
              units: this.displayUnits
            }),
          ];
        }
      }
    };

    const plugins = {
      tooltip,
      legend: {
        display: false
      },
      title: {
        display: true,
        text: t('profile.elevation-profile'),
        fontSize: 14
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'xy',
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'y',
        }
      }
    };

    const onHover = (_, items) => {
      if (items.length) {
        this.pointProfile.showSphere();
      } else {
        this.pointProfile.hideSphere();
      }
    };

    const otherOptions = {
      maintainAspectRatio: false,
      legend: {display: false},
      animation: {duration: 0},
      hover: {animationDuration: 0},
      responsive: true,
      responsiveAnimationDuration: 0
    };

    const dataset = {
      label: "",
      backgroundColor: 'rgb(255, 99, 132)',
      borderColor: 'rgb(255, 99, 132)',
      pointHoverBackgroundColor: 'rgb(0, 156, 123)',
      pointHoverBorderColor: 'rgb(0, 156, 123)',
      data: []
    };

    this.chart?.destroy();
    this.chart = new Chart(canvas, {
      type: 'scatter',
      data: {
        datasets: [dataset]
      },
      options: {
        onHover,
        plugins,
        scales,
        ...otherOptions
      } as any
    });

    this.updateUnits(units);
    this.updateSampling(type);
  }

  convert(value) {
    return this.converter.convert(
      value, "m", this.displayUnits, 2);
  }

  destroy() {
    this.chart?.destroy();
  }

  setData(data) {
    this.data = data;
    this.updateChart();
  }

  hide() {
    this.setPointProfileOpen(false);
    this.pointProfile.hideSphere();
  }

  show() {
    this.setPointProfileOpen(true);
  }

  resetZoom() {
    if (!this.chart) return;
    this.chart.resetZoom();
  }

  getSampledData() {
    let pointcloud = this.viewer.pointclouds;
    let highest = this.samplingType === "highest";
    let lineLength = this.pointProfile.line2D.distance();

    let distance = lineLength / this.samplingRate;
    distance = Math.max(this.sampleMinimum, distance);
    distance = Math.min(this.sampleMaximum, distance);

    const callback = point => {
      return Math.floor(point.distance / distance);
    };

    return pointcloud.getSampledPoints(this.data, highest, callback);
  }

  updateChart() {
    if (!this.chart) return;

    const needsSampling = this.samplingType !== "all";

    this.sampled = needsSampling
      ? this.getSampledData()
      : this.data;

    const numPoints = this.sampled.length;
    const chartTitle = needsSampling
      ? t('profile.elevation-profile-sampled-points', {numPoints})
      : t('profile.elevation-profile-points', {numPoints});

    // Convert to distance + projected height
    const points = this.sampled.map(vector => {
      const coordinate = new SceneCoordinate(vector)
        .toDataProjection();

      const height = coordinate.z;
      const distance = vector.distance;
      return new Vector2(distance, height);
    });

    // Update chart with new data
    this.chart.options.plugins.title.text = chartTitle;
    this.chart.data.datasets[0].data = points;
    this.chart.update();
  }

  /** Update units and refresh chart ui */
  updateUnits(units) {
    this.displayUnits = units;

    if (!this.chart) return;
    this.chart.update();
  }

  updateSampling(type) {
    this.samplingType = type;
    this.updateChart();
  }
}
